import {
  AlertCircle,
  ChevronDown,
  Download,
  Filter,
  Gift,
  Target,
  TrendingUp,
  TrendingDown,
  Wallet,
} from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Asset, Debt, SavingsGoal, Transaction } from '../lib/types';
import { cn, formatCurrency } from '../lib/utils';

interface OverviewTabProps {
  exchangeRate: number;
  localCurrency: string;
  transactions: Transaction[];
  setIsExchangeRateModalOpen: (isOpen: boolean) => void; // This will now open settings
  summary: { totalIncome: number; totalExpenses: number; balance: number };
  categoryData: { name: string; budget: number; actual: number }[];
  assets: Asset[];
  debts: Debt[];
  supportRelationshipData: { name: string; value: number }[];
  supportDistributionData: { name: string; value: number }[];
  selectedMonth: string;
  savingsGoals: SavingsGoal[];
  onManageGoals: () => void;

  onUpdateGoal: (id: string, data: Partial<SavingsGoal>) => Promise<void>;
  handleAddSavingsGoal: (
    goal: Omit<SavingsGoal, 'id' | 'uid'>
  ) => Promise<void>;
  handleDeleteSavingsGoal: (id: string) => Promise<void>;
  onExport: () => void;
  isPrivacyMode: boolean;
  dateFilter: 'month' | '90days' | 'ytd' | 'lastYear' | 'all';
  setDateFilter: (
    filter: 'month' | '90days' | 'ytd' | 'lastYear' | 'all'
  ) => void;
  graphColors: string[];
}

export function OverviewTab({
  exchangeRate,
  localCurrency,
  transactions,
  setIsExchangeRateModalOpen,
  summary,
  categoryData,
  assets,
  debts,
  supportRelationshipData,
  supportDistributionData,
  selectedMonth,
  savingsGoals,
  onManageGoals,
  onExport,
  isPrivacyMode,
  dateFilter,
  setDateFilter,
  graphColors,
}: OverviewTabProps) {
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const colors = useMemo(
    () =>
      graphColors.length > 0
        ? graphColors
        : ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'],
    [graphColors]
  );

  const filterOptions = [
    { id: 'month', label: 'Monthly' },
    { id: '90days', label: 'Last 90 Days' },
    { id: 'ytd', label: 'Year to Date' },
    { id: 'lastYear', label: 'Last Year' },
    { id: 'all', label: 'All Time' },
  ] as const;

  const trendData = useMemo(() => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const today = new Date(year, month - 1); // Month is 0-indexed
    const last6Months = Array.from({ length: 6 }, (_, i) => {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      return d.toISOString().slice(0, 7); // YYYY-MM
    }).reverse();

    const dataMap = last6Months.reduce(
      (acc, month) => {
        // Safely handle date parsing for label
        const [y, m] = month.split('-').map(Number);
        const dateObj = new Date(y, m - 1);
        const label = dateObj.toLocaleString('default', { month: 'short' });

        acc[month] = {
          name: label,
          income: 0,
          expense: 0,
        };
        return acc;
      },
      {} as Record<string, { name: string; income: number; expense: number }>
    );

    transactions.forEach((t) => {
      const monthKey = t.date.slice(0, 7);
      if (dataMap[monthKey]) {
        const amount =
          t.category === 'Overseas Expenses'
            ? t.amount / (exchangeRate || 1)
            : t.amount;
        if (t.type === 'income') dataMap[monthKey].income += amount;
        else if (t.type === 'expense') dataMap[monthKey].expense += amount;
      }
    });

    return Object.values(dataMap);
  }, [transactions, exchangeRate, selectedMonth]);

  const wealthSummary = useMemo(() => {
    const totalAssets = assets.reduce((sum, a) => sum + a.value, 0);
    const totalDebts = debts.reduce((sum, d) => sum + d.currentBalance, 0);
    const netWorth = totalAssets - totalDebts;
    return { totalAssets, totalDebts, netWorth };
  }, [assets, debts]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between px-1">
        <h2 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">
          Overview
        </h2>
        <div className="flex gap-2">
          <div className="relative">
            <button
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className="flex items-center gap-2 rounded-xl border border-zinc-200 bg-white px-4 py-2 text-xs font-bold tracking-wider text-zinc-700 uppercase shadow-sm transition-colors hover:bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800"
            >
              <Filter className="h-4 w-4" />
              <span>Filter</span>
              <ChevronDown className="h-3 w-3 opacity-50" />
            </button>
            <AnimatePresence>
              {isFilterOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setIsFilterOpen(false)}
                  />
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    className="absolute top-full right-0 z-20 mt-2 flex w-48 flex-col gap-0.5 rounded-2xl border border-zinc-100 bg-white p-1.5 shadow-xl dark:border-zinc-800 dark:bg-zinc-900"
                  >
                    {filterOptions.map((option) => (
                      <button
                        key={option.id}
                        onClick={() => {
                          setDateFilter(option.id);
                          setIsFilterOpen(false);
                        }}
                        className={cn(
                          'flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left text-xs font-bold transition-colors',
                          dateFilter === option.id
                            ? 'bg-zinc-100 text-zinc-900 dark:bg-zinc-800 dark:text-zinc-100'
                            : 'text-zinc-500 hover:bg-zinc-50 hover:text-zinc-900 dark:hover:bg-zinc-800/50 dark:hover:text-zinc-300'
                        )}
                      >
                        {option.label}
                        {dateFilter === option.id && (
                          <div className="h-1.5 w-1.5 rounded-full bg-indigo-600" />
                        )}
                      </button>
                    ))}
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
          <button
            onClick={onExport}
            className="flex items-center gap-2 rounded-xl bg-zinc-900 px-4 py-2 text-xs font-bold tracking-wider text-white uppercase shadow-sm transition-opacity hover:opacity-90 dark:bg-zinc-100 dark:text-zinc-900"
          >
            <Download className="h-4 w-4" />
            Export
          </button>
        </div>
      </div>
      {exchangeRate === 1 &&
        transactions.some((t) => t.category === 'Overseas Expenses') && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-3 rounded-2xl border border-amber-100 bg-amber-50 p-4"
          >
            <AlertCircle className="h-5 w-5 shrink-0 text-amber-500" />
            <div className="flex-1">
              <p className="text-xs font-bold text-amber-900">
                Exchange Rate Not Set
              </p>
              <p className="text-[10px] text-amber-700">
                Support transactions are currently using 1:1 rate. Please set
                the monthly rate.
              </p>
            </div>
            <button
              onClick={() => setIsExchangeRateModalOpen(true)}
              className="rounded-xl bg-amber-500 px-3 py-1.5 text-[10px] font-bold tracking-wider text-white uppercase shadow-sm transition-transform active:scale-95"
            >
              Set Rate
            </button>
          </motion.div>
        )}
      {/* Summary Card */}
      <div className="rounded-[32px] bg-zinc-900 p-8 text-white shadow-2xl shadow-zinc-200 transition-colors dark:bg-zinc-800 dark:shadow-zinc-900/50">
        <div className="space-y-2 text-center">
          <p className="text-[10px] font-bold tracking-widest text-zinc-400 uppercase">
            Total Balance
          </p>
          <h2
            className={cn(
              'text-4xl font-bold tracking-tight',
              summary.balance < 0 ? 'text-rose-400' : 'text-white',
              isPrivacyMode && 'blur-md select-none'
            )}
          >
            {formatCurrency(summary.balance, localCurrency)}
          </h2>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-4 border-t border-white/10 pt-8">
          <div className="flex flex-col items-center">
            <div className="mb-1 flex items-center gap-1.5">
              <div className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
              <p className="text-[10px] font-bold tracking-widest text-zinc-400 uppercase">
                Income
              </p>
            </div>
            <p
              className={cn(
                'text-xl font-bold text-emerald-400',
                isPrivacyMode && 'blur-sm select-none'
              )}
            >
              {formatCurrency(summary.totalIncome, localCurrency)}
            </p>
          </div>
          <div className="flex flex-col items-center border-l border-white/10">
            <div className="mb-1 flex items-center gap-1.5">
              <div className="h-1.5 w-1.5 rounded-full bg-rose-400" />
              <p className="text-[10px] font-bold tracking-widest text-zinc-400 uppercase">
                Expenses
              </p>
            </div>
            <p
              className={cn(
                'text-xl font-bold text-rose-400',
                isPrivacyMode && 'blur-sm select-none'
              )}
            >
              {formatCurrency(summary.totalExpenses, localCurrency)}
            </p>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="space-y-6">
        <div className="rounded-3xl border border-zinc-100 bg-white p-6 shadow-sm transition-colors dark:border-zinc-800 dark:bg-zinc-900">
          <h3 className="mb-8 text-base font-bold text-zinc-800 dark:text-zinc-200">
            Expenditure by Category
          </h3>
          <div className="h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={categoryData} margin={{ bottom: 40 }}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="#f1f5f9"
                  className="dark:opacity-10"
                />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 10, fontWeight: 500, fill: '#94a3b8' }}
                  axisLine={false}
                  tickLine={false}
                  angle={-45}
                  textAnchor="end"
                  interval={0}
                />
                <YAxis
                  tick={{ fontSize: 10, fontWeight: 500, fill: '#94a3b8' }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(value) => `$${value}`}
                />
                <Tooltip
                  cursor={{ fill: '#f8fafc' }}
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      const value = payload[0].value as number;
                      const percentage =
                        summary.totalExpenses > 0
                          ? ((value / summary.totalExpenses) * 100).toFixed(1)
                          : 0;
                      return (
                        <div className="z-50 flex min-w-[140px] flex-col items-start gap-1 rounded-2xl bg-zinc-900 px-4 py-3 text-xs font-bold text-white shadow-xl dark:bg-zinc-800">
                          <span className="text-[10px] font-bold tracking-wider text-zinc-400 uppercase">
                            {data.name}
                          </span>
                          <span
                            className={cn(
                              'text-xl font-bold',
                              isPrivacyMode && 'blur-sm'
                            )}
                          >
                            {formatCurrency(value, localCurrency)}
                          </span>
                          <span className="text-[10px] font-medium text-zinc-400">
                            {percentage}% of total
                          </span>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Bar
                  dataKey="actual"
                  fill={colors[0]}
                  radius={[6, 6, 0, 0]}
                  barSize={24}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {[
            {
              title: 'Support: Relatives vs Acquaintances',
              data: supportRelationshipData,
            },
            {
              title: 'Support Distribution',
              data: supportDistributionData,
              offset: 2,
            },
          ].map((chart, i) => {
            const chartTotal = chart.data.reduce(
              (acc, item) => acc + item.value,
              0
            );

            return (
              <div
                key={i}
                className="rounded-3xl border border-zinc-100 bg-white p-6 shadow-sm transition-colors dark:border-zinc-800 dark:bg-zinc-900"
              >
                <h3 className="mb-8 text-base font-bold text-zinc-800 dark:text-zinc-200">
                  {chart.title}
                </h3>
                <div className="flex h-[260px] items-center justify-center">
                  {chart.data.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={chart.data}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={80}
                          paddingAngle={5}
                          dataKey="value"
                        >
                          {chart.data.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={
                                colors[
                                  (index + (chart.offset || 0)) % colors.length
                                ]
                              }
                            />
                          ))}
                        </Pie>
                        <Tooltip
                          content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                              const value = Number(payload[0].value);
                              const percentage =
                                chartTotal > 0
                                  ? ((value / chartTotal) * 100).toFixed(1)
                                  : 0;
                              return (
                                <div className="z-50 flex min-w-[140px] flex-col items-start gap-1 rounded-2xl bg-zinc-900 px-4 py-3 text-xs font-bold text-white shadow-xl dark:bg-zinc-800">
                                  <span className="text-[10px] font-bold tracking-wider text-zinc-400 uppercase">
                                    {payload[0].name}
                                  </span>
                                  <span
                                    className={cn(
                                      'text-xl font-bold',
                                      isPrivacyMode && 'blur-sm'
                                    )}
                                  >
                                    {formatCurrency(value, localCurrency)}
                                  </span>
                                  <span className="text-[10px] font-medium text-zinc-400">
                                    {percentage}% of total
                                  </span>
                                </div>
                              );
                            }
                            return null;
                          }}
                        />
                        <Legend verticalAlign="bottom" height={36} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="space-y-2 text-center">
                      <Gift className="mx-auto h-8 w-8 text-zinc-200" />
                      <p className="text-xs font-medium text-zinc-400 italic">
                        No data available
                      </p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="rounded-3xl border border-zinc-100 bg-white p-6 shadow-sm transition-colors dark:border-zinc-800 dark:bg-zinc-900">
          <h3 className="mb-8 text-base font-bold text-zinc-800 dark:text-zinc-200">
            Financial Trend (6 Months)
          </h3>
          <div className="h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={trendData}
                margin={{ top: 10, right: 0, left: -20, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="colorIncome" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.1} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorExpense" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.1} />
                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="#f1f5f9"
                  className="dark:opacity-10"
                />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 10, fontWeight: 500, fill: '#94a3b8' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 10, fontWeight: 500, fill: '#94a3b8' }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(value) => `$${value}`}
                />
                <Tooltip
                  cursor={{
                    stroke: '#94a3b8',
                    strokeWidth: 1,
                    strokeDasharray: '3 3',
                  }}
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div className="z-50 flex min-w-[140px] flex-col gap-1 rounded-2xl bg-zinc-900 px-4 py-3 text-xs font-bold text-white shadow-xl dark:bg-zinc-800">
                          <span className="mb-1 text-[10px] font-bold tracking-wider text-zinc-400 uppercase">
                            {label}
                          </span>
                          <div className="flex w-full justify-between text-emerald-400">
                            <span>Income:</span>{' '}
                            <span className={cn(isPrivacyMode && 'blur-sm')}>
                              {formatCurrency(
                                Number(payload[0].value),
                                localCurrency
                              )}
                            </span>
                          </div>
                          <div className="flex w-full justify-between text-rose-400">
                            <span>Expenses:</span>{' '}
                            <span className={cn(isPrivacyMode && 'blur-sm')}>
                              {formatCurrency(
                                Number(payload[1].value),
                                localCurrency
                              )}
                            </span>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="income"
                  stroke="#10b981"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorIncome)"
                />
                <Area
                  type="monotone"
                  dataKey="expense"
                  stroke="#ef4444"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorExpense)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between px-1">
            <h3 className="text-lg font-bold text-zinc-900 dark:text-zinc-100">
              Savings Goals
            </h3>
            <button
              onClick={onManageGoals}
              className="text-xs font-bold text-indigo-600 transition-opacity hover:opacity-80 dark:text-indigo-400"
            >
              Manage Goals
            </button>
          </div>
          {savingsGoals.length === 0 ? (
            <button
              onClick={onManageGoals}
              className="flex w-full flex-col items-center justify-center gap-2 rounded-3xl border-2 border-dashed border-zinc-200 py-8 text-zinc-400 transition-all hover:border-indigo-200 hover:bg-indigo-50/50 dark:border-zinc-800 dark:hover:border-indigo-900 dark:hover:bg-indigo-900/10"
            >
              <Target className="h-6 w-6" />
              <span className="text-xs font-bold">
                Add your first savings goal
              </span>
            </button>
          ) : (
            <div
              className="no-scrollbar -mx-6 flex snap-x gap-4 overflow-x-auto px-6 pb-2"
              style={{ msOverflowStyle: 'none', scrollbarWidth: 'none' }}
            >
              {savingsGoals.map((goal) => {
                const percentage = Math.min(
                  (goal.currentAmount / goal.targetAmount) * 100,
                  100
                );
                return (
                  <div
                    key={goal.id}
                    className="flex min-w-[160px] snap-center flex-col items-center gap-3 rounded-3xl border border-zinc-100 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-900"
                  >
                    <div className="relative flex h-16 w-16 items-center justify-center">
                      <svg className="h-full w-full -rotate-90 transform">
                        <circle
                          cx="32"
                          cy="32"
                          r="28"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="transparent"
                          className="text-zinc-100 dark:text-zinc-800"
                        />
                        <circle
                          cx="32"
                          cy="32"
                          r="28"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="transparent"
                          strokeDasharray={175.9}
                          strokeDashoffset={175.9 - (175.9 * percentage) / 100}
                          className="text-emerald-500 transition-all duration-1000 ease-out"
                          strokeLinecap="round"
                        />
                      </svg>
                      <span className="absolute text-xs font-bold text-zinc-900 dark:text-zinc-100">
                        {percentage.toFixed(0)}%
                      </span>
                    </div>
                    <div className="text-center">
                      <p className="max-w-[120px] truncate text-xs font-bold text-zinc-900 dark:text-zinc-100">
                        {goal.name}
                      </p>
                      <p
                        className={cn(
                          'mt-0.5 text-[10px] text-zinc-500 dark:text-zinc-400',
                          isPrivacyMode && 'blur-sm select-none'
                        )}
                      >
                        {formatCurrency(goal.currentAmount, localCurrency)} /{' '}
                        {formatCurrency(goal.targetAmount, localCurrency)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Wealth Summary Card */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="flex items-center gap-4 rounded-[24px] border border-zinc-100 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
          <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 dark:bg-indigo-900/20 dark:text-indigo-400">
            <Wallet className="h-5 w-5" />
          </div>
          <div>
            <p className="text-[10px] font-bold tracking-widest text-zinc-400 uppercase">
              Net Worth
            </p>
            <p
              className={cn(
                'text-lg font-bold text-zinc-900 dark:text-zinc-100',
                isPrivacyMode && 'blur-sm select-none'
              )}
            >
              {formatCurrency(wealthSummary.netWorth, localCurrency)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4 rounded-[24px] border border-zinc-100 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
          <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400">
            <TrendingUp className="h-5 w-5" />
          </div>
          <div>
            <p className="text-[10px] font-bold tracking-widest text-zinc-400 uppercase">
              Assets
            </p>
            <p
              className={cn(
                'text-lg font-bold text-zinc-900 dark:text-zinc-100',
                isPrivacyMode && 'blur-sm select-none'
              )}
            >
              {formatCurrency(wealthSummary.totalAssets, localCurrency)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4 rounded-[24px] border border-zinc-100 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
          <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-rose-50 text-rose-600 dark:bg-rose-900/20 dark:text-rose-400">
            <TrendingDown className="h-5 w-5" />
          </div>
          <div>
            <p className="text-[10px] font-bold tracking-widest text-zinc-400 uppercase">
              Liabilities
            </p>
            <p
              className={cn(
                'text-lg font-bold text-zinc-900 dark:text-zinc-100',
                isPrivacyMode && 'blur-sm select-none'
              )}
            >
              {formatCurrency(wealthSummary.totalDebts, localCurrency)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
